<template>
    <div
        v-if="isStitchFile"
        class="paa-field"
        data-testid="prior-approval-field"
    >
        <div class="paa-row">
            <label class="paa-check">
                <input
                    type="checkbox"
                    :checked="modelValue.attested"
                    :disabled="disabled"
                    data-testid="prior-approval-checkbox"
                    @change="onToggle($event.target.checked)"
                />
                <span>This file was already approved by</span>
            </label>
            <select
                class="paa-attester"
                :value="attester"
                :disabled="disabled"
                aria-label="Who approved it"
                data-testid="prior-approval-attester"
                @change="onAttesterChange($event.target.value)"
            >
                <option :value="ATTESTER_CLIENT">client</option>
                <option :value="ATTESTER_TEAM_MEMBER">team member</option>
            </select>
        </div>
        <template v-if="modelValue.attested && !isTeam">
            <v-select
                :model-value="modelValue.clientId"
                :items="clientOptions"
                item-title="name"
                item-value="id"
                label="Client who approved it"
                density="compact"
                variant="outlined"
                hide-details
                :loading="clientsStore.loading"
                :disabled="disabled"
                :aria-describedby="clientHintId"
                data-testid="prior-approval-client"
                @update:model-value="onClientPicked"
            />
            <span
                v-if="noClients"
                :id="clientHintId"
                class="paa-hint paa-hint--warn"
                data-testid="prior-approval-no-clients"
            >
                You have no clients yet. Add the client first, then upload
                this file.
            </span>
            <span v-else :id="clientHintId" class="paa-hint">
                It won't be sent to that client for approval again.
            </span>
        </template>
        <template v-if="modelValue.attested && isTeam">
            <v-select
                :model-value="modelValue.memberId"
                :items="members"
                item-title="name"
                item-value="id"
                label="Team member who approved it"
                density="compact"
                variant="outlined"
                hide-details
                :loading="membersLoading"
                :disabled="disabled || !canNameOthers"
                :aria-describedby="memberHintIds"
                data-testid="prior-approval-member"
                @update:model-value="onMemberPicked"
            />
            <span
                v-if="noMembers"
                :id="memberHintId"
                class="paa-hint paa-hint--warn"
                data-testid="prior-approval-no-members"
            >
                {{ membersError || 'No active team members were found.' }}
            </span>
            <span v-else :id="memberHintId" class="paa-hint">
                It counts as approved for every client that uses it, at the
                size the file was made at, and won't be sent for approval.
            </span>
            <span
                v-if="selfOnly"
                :id="selfOnlyHintId"
                class="paa-hint"
                data-testid="prior-approval-member-self-only"
            >
                Only an Owner can record another team member's approval.
            </span>
        </template>
    </div>
</template>

<script>
/**
 * PriorApprovalAttestation.vue -- "This file was already approved by
 * [client|team member]" for an uploaded embroidery stitch file (.dst/.emb),
 * with the client or team member who approved it.
 *
 * Card: Bug Intake #332 -- https://trello.com/c/fstQk6AK (GH #4292).
 * Team member: Jake, 2026-10-27 -- "if you pick 'team member' the picker
 * lists members of the distributor's team, defaulting to the currently
 * signed in member."
 *
 * Distributors bring libraries of stitch files approved long before
 * ThreadFlow. This captures that fact at upload; the upload then records
 * it via POST /api/artworks/<id>/prior-approval (see
 * ``useArtworksStore().uploadArtwork`` and ``utils/priorApproval``). It is
 * not an override: nothing is skipped that was not already done. A team
 * member's statement counts for every client that uses the file, at the
 * file's own size (the backend mints each store client's approval then).
 * Only an Owner (or a Super Admin) may name another member: everyone else
 * is offered only themself (the list's ``can_name_others``), matching the
 * backend's 403 for naming a teammate.
 *
 * v-model is ``{ attested, attester, clientId, memberId }``
 * (``utils/priorApproval``). Renders nothing for any other file type, and
 * resets the value when the file changes to one.
 */
import { useArtworksStore } from '@/stores/artworks'
import { useClientsStore } from '@/stores/clients'
import { isEmbroiderySource } from '@/utils/artworkUploadContract'
import {
    ATTESTER_CLIENT,
    ATTESTER_TEAM_MEMBER,
    clearedPriorApproval,
} from '@/utils/priorApproval'

// Per-instance ids for aria-describedby (two upload surfaces can mount it).
let instanceSeq = 0

export default {
    name: 'PriorApprovalAttestation',

    props: {
        modelValue: {
            type: Object,
            default: () => clearedPriorApproval(),
        },
        /** The chosen file's name; the control shows only for .dst/.emb. */
        fileName: { type: String, default: '' },
        disabled: { type: Boolean, default: false },
        /** Pre-selected client when ticked (e.g. the store's own client). */
        defaultClientId: { type: [Number, String], default: null },
        /**
         * The artwork's distributor. Only its clients and team members are
         * offered: a Super Admin's client list spans every tenant, and the
         * backend refuses a client or member from another distributor.
         */
        distributorId: { type: [Number, String], default: null },
    },

    emits: ['update:modelValue'],

    setup() {
        return {
            clientsStore: useClientsStore(),
            artworksStore: useArtworksStore(),
            ATTESTER_CLIENT,
            ATTESTER_TEAM_MEMBER,
        }
    },

    data() {
        const uid = `paa-${++instanceSeq}`
        return {
            clientHintId: `${uid}-client-hint`,
            memberHintId: `${uid}-member-hint`,
            selfOnlyHintId: `${uid}-member-self-only`,
            // Whether the caller may name someone other than themself (an
            // Owner or a Super Admin). False until the list says otherwise,
            // matching the backend rule.
            canNameOthers: false,
            // The team members of ``membersFor``'s distributor, as
            // [{ id, name, is_me }]. ``membersFor`` is undefined until a
            // fetch lands (null = the caller's own tenant).
            members: [],
            membersFor: undefined,
            membersLoading: false,
            membersLoadingFor: undefined,
            membersError: null,
            // Bumped per fetch, so a stale response (the distributor
            // changed mid-flight) is ignored.
            membersRequest: 0,
            // True while a ticked team-member choice waits for the list to
            // preselect the signed-in member. Any choice the user makes in
            // the meantime (a member, client, unticking) clears it, so a
            // late list never overrides the user.
            awaitingMemberDefault: false,
        }
    },

    computed: {
        isStitchFile() {
            return isEmbroiderySource(this.fileName)
        },
        attester() {
            return this.modelValue?.attester === ATTESTER_TEAM_MEMBER
                ? ATTESTER_TEAM_MEMBER
                : ATTESTER_CLIENT
        },
        isTeam() {
            return this.attester === ATTESTER_TEAM_MEMBER
        },
        tenantKey() {
            return this.distributorId == null || this.distributorId === ''
                ? null
                : String(this.distributorId)
        },
        clientOptions() {
            const tenant = this.tenantKey
            return this.clientsStore.clients
                .filter((c) => c.status !== 'inactive')
                .filter((c) => tenant == null || String(c.distributor_id) === tenant)
                .map((c) => ({ id: Number(c.id), name: c.name }))
        },
        noClients() {
            return !this.clientsStore.loading && this.clientOptions.length === 0
        },
        noMembers() {
            return (
                !this.membersLoading &&
                this.membersFor === this.tenantKey &&
                this.members.length === 0
            )
        },
        /** A non-Owner's list: only themself, so the picker is fixed. */
        selfOnly() {
            return (
                !this.canNameOthers &&
                this.membersFor === this.tenantKey &&
                this.members.length > 0
            )
        },
        memberHintIds() {
            return this.selfOnly
                ? `${this.memberHintId} ${this.selfOnlyHintId}`
                : this.memberHintId
        },
        /** The signed-in member, when they are on this team. */
        defaultMemberId() {
            const me = this.members.find((m) => m.is_me)
            return me ? Number(me.id) : null
        },
    },

    watch: {
        isStitchFile(isStitch) {
            const v = this.modelValue || {}
            if (!isStitch && (v.attested || v.attester === ATTESTER_TEAM_MEMBER)) {
                this.$emit('update:modelValue', clearedPriorApproval())
            }
        },
        // Another distributor's team is another list: re-fetch, and
        // re-default a ticked team-member choice to the new list.
        tenantKey() {
            this.members = []
            this.canNameOthers = false
            this.membersFor = undefined
            if (this.modelValue?.attested && this.isTeam) {
                this.$emit('update:modelValue', this.ticked(ATTESTER_TEAM_MEMBER))
                this.loadMembers()
            }
        },
    },

    methods: {
        /** The ticked value for ``attester``, preset to its default pick. */
        ticked(attester) {
            if (attester === ATTESTER_TEAM_MEMBER) {
                const loaded = this.membersFor === this.tenantKey
                this.awaitingMemberDefault = !loaded
                return {
                    attested: true,
                    attester,
                    clientId: null,
                    memberId: loaded ? this.defaultMemberId : null,
                }
            }
            this.awaitingMemberDefault = false
            const preset =
                this.defaultClientId != null && this.defaultClientId !== ''
                    ? Number(this.defaultClientId)
                    : null
            return { attested: true, attester, clientId: preset, memberId: null }
        },
        load(attester) {
            if (attester === ATTESTER_TEAM_MEMBER) {
                this.loadMembers()
            } else if (this.clientsStore.clients.length === 0) {
                this.clientsStore.fetch()
            }
        },
        onToggle(checked) {
            if (!checked) {
                this.awaitingMemberDefault = false
                this.$emit('update:modelValue', {
                    ...clearedPriorApproval(),
                    attester: this.attester,
                })
                return
            }
            this.$emit('update:modelValue', this.ticked(this.attester))
            this.load(this.attester)
        },
        onAttesterChange(value) {
            const attester =
                value === ATTESTER_TEAM_MEMBER ? ATTESTER_TEAM_MEMBER : ATTESTER_CLIENT
            if (!this.modelValue?.attested) {
                this.awaitingMemberDefault = false
                this.$emit('update:modelValue', {
                    ...clearedPriorApproval(),
                    attester,
                })
                return
            }
            this.$emit('update:modelValue', this.ticked(attester))
            this.load(attester)
        },
        onClientPicked(id) {
            this.$emit('update:modelValue', {
                attested: true,
                attester: ATTESTER_CLIENT,
                clientId: id == null || id === '' ? null : Number(id),
                memberId: null,
            })
        },
        onMemberPicked(id) {
            this.awaitingMemberDefault = false
            this.$emit('update:modelValue', {
                attested: true,
                attester: ATTESTER_TEAM_MEMBER,
                clientId: null,
                memberId: id == null || id === '' ? null : Number(id),
            })
        },
        async loadMembers() {
            const tenant = this.tenantKey
            if (this.membersFor === tenant) return
            if (this.membersLoading && this.membersLoadingFor === tenant) return
            const request = ++this.membersRequest
            this.membersLoading = true
            this.membersLoadingFor = tenant
            this.membersError = null
            try {
                const team = await this.artworksStore.fetchPriorApprovalTeamMembers(
                    tenant == null ? null : Number(tenant),
                )
                if (request !== this.membersRequest || tenant !== this.tenantKey) {
                    return
                }
                this.canNameOthers = team?.canNameOthers === true
                this.members = (team?.members || []).map((m) => ({
                    id: Number(m.id),
                    name: m.name,
                    is_me: Boolean(m.is_me),
                }))
                this.membersFor = tenant
            } catch (err) {
                if (request !== this.membersRequest) return
                this.canNameOthers = false
                this.members = []
                this.membersFor = tenant
                this.membersError =
                    err?.response?.data?.error ||
                    'The team could not be loaded. Try again, or pick a client.'
            } finally {
                if (request === this.membersRequest) this.membersLoading = false
            }
            // Preselect the signed-in member, unless the user chose
            // anything while the list loaded.
            if (this.awaitingMemberDefault) {
                this.onMemberPicked(this.defaultMemberId)
            }
        },
    },
}
</script>

<style scoped>
.paa-field {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.paa-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
}

.paa-attester {
    font-size: 14px;
    padding: 2px 6px;
    border: 1px solid rgba(0, 0, 0, 0.38);
    border-radius: 4px;
    background: #fff;
}

.paa-check {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    cursor: pointer;
}

.paa-hint {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.6);
}

.paa-hint--warn {
    color: #b26a00;
}
</style>
